import PubNub from "pubnub";
import { v4 as uuidv4 } from "uuid";
// import { PresenceEvent } from "pubnub";

export interface PubNubMessage {
  id: string;
  channel: string;
  message: string;
  sender: string;
  timestamp: number;
}

export class PubNubService {
  private pubnub: PubNub;

  constructor(publishKey: string, subscribeKey: string, userId: string) {
    this.pubnub = new PubNub({
      publishKey,
      subscribeKey,
      userId,
    });
  }

  // Publish a message to a channel
  async publishMessage(
    channel: string,
    message: string
  ): Promise<PubNubMessage> {
    const messageObject: PubNubMessage = {
      id: uuidv4(),
      channel,
      message,
      sender: this.pubnub.getUUID(),
      timestamp: Date.now(),
    };

    await this.pubnub.publish({
      channel,
      message: messageObject as unknown as PubNub.Payload,
    });

    return messageObject;
  }

  // Subscribe to one or more channels
  subscribeToChannels(
    channels: string[],
    listener: (message: PubNubMessage) => void
  ): void {
    this.pubnub.subscribe({ channels });

    this.pubnub.addListener({
      message: (event) => {
        listener(event.message as unknown as PubNubMessage);
      },
    });
  }

  // Unsubscribe from one or more channels
  unsubscribeFromChannels(channels: string[]): void {
    this.pubnub.unsubscribe({ channels });
  }

  // Get message history for a channel
  async getMessageHistory(
    channel: string,
    count: number = 100
  ): Promise<PubNubMessage[]> {
    const result = await this.pubnub.history({
      channel,
      count,
    });

    return result.messages.map(
      (item) => item.entry as unknown as PubNubMessage
    );
  }

  // Handle presence (online/offline status)
  subscribeToPresence(
    channels: string[],
    listener: (presenceEvent: any) => void
  ): void {
    this.pubnub.subscribe({ channels, withPresence: true });

    this.pubnub.addListener({
      presence: (presenceEvent) => {
        listener(presenceEvent);
      },
    });
  }

  // Get users present in a channel
  async getUsersInChannel(channel: string): Promise<string[]> {
    const result = await this.pubnub.hereNow({
      channels: [channel],
    });

    return result.channels[channel].occupants.map((occupant) => occupant.uuid);
  }

  // Send a typing indicator
  async sendTypingIndicator(channel: string): Promise<void> {
    await this.pubnub.signal({
      channel,
      message: { action: "typing" } as unknown as PubNub.Payload,
    });
  }

  // Create a new chat room (channel)
  async createChatRoom(roomName: string): Promise<string> {
    const channelId = `chat_${uuidv4()}`;
    await this.pubnub.objects.setChannelMetadata({
      channel: channelId,
      data: { name: roomName },
    });
    return channelId;
  }

  // Get all chat rooms (channels)
  async getChatRooms(): Promise<{ id: string; name: string }[]> {
    const result = await this.pubnub.objects.getAllChannelMetadata();
    return result.data.map((channel) => ({
      id: channel.id,
      name: channel.name || "",
    }));
  }

  // Add user to a chat room (channel)
  async addUserToChatRoom(channelId: string, userId: string): Promise<void> {
    await this.pubnub.objects.setMemberships({
      uuid: userId,
      channels: [channelId],
    });
  }

  // Remove user from a chat room (channel)
  async removeUserFromChatRoom(
    channelId: string,
    userId: string
  ): Promise<void> {
    await this.pubnub.objects.removeMemberships({
      uuid: userId,
      channels: [channelId],
    });
  }
}

export function initPubNub(
  publishKey: string,
  subscribeKey: string,
  userId: string
): PubNubService {
  return new PubNubService(publishKey, subscribeKey, userId);
}
